'use client';

import { useState, useMemo, useEffect, useRef } from 'react';
import { RoomEvent, DataPacket_Kind } from 'livekit-client';
import { useRoomContext, useVoiceAssistant } from '@livekit/components-react';
import { useSession } from '@/components/app/session-provider';
import { toastAlert } from '@/components/livekit/alert-toast';
import { EmbedWelcomeView } from '@/components/app/embed-welcome-view';
import { useChatMessages } from '@/hooks/useChatMessages';

type AnyMsg = {
  type?: string;
  url?: string;
  redirectUrl?: string;
  module?: string;
  message?: string;
};


export function EmbedViewController() {
  const { appConfig, isSessionActive, startSession, endSession } = useSession();
  const { videoTrack: agentVideoTrack } = useVoiceAssistant();
  const room = useRoomContext();

  // Warmup gating
  const [warmupEnded, setWarmupEnded] = useState(false);
  const warmupEndedSentRef = useRef(false);

  const agentReadyRef = useRef(false);
  const videoEndedRef = useRef(false);

  // Progress bar state for interaction window
  const AVATAR_INTERACTION_WINDOW_SECONDS = 4 * 60; // 4 minutes
  const SHOW_TIME_UP_WARNING_TIME_SECONDS = AVATAR_INTERACTION_WINDOW_SECONDS - 30; // warn at 30s remaining
  const TIME_UP_WARNING_DURATION_SECONDS = 5; // fade-out duration

  const [progressBar, setProgressBar] = useState<number>(0);
  const [fadeOut, setFadeOut] = useState<boolean>(false);

  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const warningTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningScheduledRef = useRef<boolean>(false);

  const greetingSentRef = useRef<boolean>(false);

  const messages = useChatMessages();
  const lastMessageSignatureRef = useRef<string | undefined>(undefined);

  const closureHandledRef = useRef<boolean>(false);
  const closureTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closureInstructionSentRef = useRef<boolean>(false);
  const isConnecting = isSessionActive && (!warmupEnded || !agentVideoTrack);

  // Prevent multiple redirects
  const redirectedRef = useRef<boolean>(false);

  const callButtonText = useMemo(
    () => (isSessionActive ? 'Stop Call' : 'Talk to Joy Live'),
    [isSessionActive, appConfig.startButtonText]
  );

  function safeUrlFromMsg(msg: AnyMsg): string | null {
    const u = (msg?.url || msg?.redirectUrl || '').toString().trim();
    if (!u) return null;
    if (!/^https?:\/\/[^\s]+$/i.test(u)) return null;
    return u;
  }

  // Works for both non-embedded & embedded
  function universalRedirect(url: string) {
    console.log('[universalRedirect] url=', url, 'embedded?', window.parent !== window);

    // If embedded, ask parent to navigate
    try {
      if (window.parent && window.parent !== window) {
        window.parent.postMessage({ action: 'redirect', url }, '*');

        // iframe fallback
        try {
          window.location.assign(url);
        } catch {}

        return;
      }
    } catch (e) {
      console.warn('[universalRedirect] postMessage failed:', e);
    }

    // Not embedded
    try {
      window.location.assign(url);
    } catch (e) {
      console.error('[universalRedirect] window.location.assign failed:', e);
    }
  }

  useEffect(() => {
    console.log('EmbedViewController mounted'); 
  }, []);

  
  useEffect(() => {
    if (!room) return;

    // Muting mic when room is available
    room.localParticipant?.setMicrophoneEnabled(false);

    room.registerRpcMethod('unmute_user', async (data) => {
      console.log('[RPC] unmute_user called', data);
      await room.localParticipant.setMicrophoneEnabled(true);
      return 'mic unmuted';
    });

    return () => {
      room.unregisterRpcMethod('unmute_user');
    };
  }, [room]); 

  function tryFireIntroVideoDone() { 
  if (!agentReadyRef.current) return;
  if (!videoEndedRef.current) return;
  if (warmupEndedSentRef.current) return;

  warmupEndedSentRef.current = true;

  try {
    const payload = new TextEncoder().encode(
      JSON.stringify({ type: 'Intro_video_done', ts: Date.now() })
    );
    room?.localParticipant?.publishData(payload, { reliable: true });
    console.log('[warmup] Sent Intro_video_done signal to backend');
  } catch (e) {
    console.warn('[warmup] Failed to publish Intro_video_done', e);
  }
  }

  const handleWarmupEnded = () => {
    setWarmupEnded(true);
    videoEndedRef.current = true;
    console.log('[warmup] Video ended, agentReady?', agentReadyRef.current);
    tryFireIntroVideoDone();
  };

  const handleStartCall = async () => {
    redirectedRef.current = false;
    agentReadyRef.current = false;
    videoEndedRef.current = false;
    // reset warmup gate
    setWarmupEnded(false);
    warmupEndedSentRef.current = false;

    try {
      await startSession();

      // Ensure mic starts muted after session begins
      room?.localParticipant?.setMicrophoneEnabled(false);

      greetingSentRef.current = false;
      closureHandledRef.current = false;
      closureInstructionSentRef.current = false;

      if (closureTimerRef.current) {
        clearTimeout(closureTimerRef.current);
        closureTimerRef.current = null;
      }

      setProgressBar(0);
      setFadeOut(false);

      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
        warningTimeoutRef.current = null;
      }

      console.log('[call] startSession complete');
    } catch (error) {
      setWarmupEnded(false);
      warmupEndedSentRef.current = false;
      agentReadyRef.current = false;
      videoEndedRef.current = false;
    }
  };

  const handleEndCall = () => {
    console.log('[call] handleEndCall');

    // Mute mic on call end
    room?.localParticipant?.setMicrophoneEnabled(false);

    // reset warmup gate
    setWarmupEnded(false);
    warmupEndedSentRef.current = false;
    agentReadyRef.current = false;
    videoEndedRef.current = false;

    if (closureTimerRef.current) {
      clearTimeout(closureTimerRef.current);
      closureTimerRef.current = null;
    }

    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }

    warningScheduledRef.current = false;
    setFadeOut(false);
    setProgressBar(0);
    closureInstructionSentRef.current = false;

    endSession();
  };

  // Redirect listener
  useEffect(() => {
    if (!room) return;

    console.log('Attaching redirect listener. room?', Boolean(room));

    const okTypes = new Set([
      'view_plan_stage',
      'view_plan_demo',
      'view_demo_team_larger_than_9',
      'view_demo_team_less_or_equal_to_9',
    ]);

    function onDataReceived(
      payload: Uint8Array,
      participant?: any,
      _kind?: DataPacket_Kind,
      topic?: string
    ) {
      let raw = '';
      try {
        raw = new TextDecoder().decode(payload);
      } catch {
        raw = '[binary payload]';
      }

      console.log(
        '[DataReceived]',
        { topic, from: participant?.identity, alreadyRedirected: redirectedRef.current },
        'raw=',
        raw
      );

      let msg: AnyMsg | null = null;
      try {
        msg = JSON.parse(raw);
      } catch {
        return;
      }

      const msgType = String(msg?.type ?? '').trim();

      if (msgType === 'agent_ready') {
        agentReadyRef.current = true;
        console.log('[warmup] agent_ready received');
        tryFireIntroVideoDone();
        return;
      }


      if (redirectedRef.current) return;

   
      const url = safeUrlFromMsg(msg || {});
      const spokenMsg = (msg?.message ?? '').toString().trim();

      console.log('[DataReceived parsed]', { msgType, url, msg });

      if (!msgType) return;
      if (!okTypes.has(msgType)) return;
      if (!url) {
        console.warn('[DataReceived] missing/invalid url fields', {
          url: msg?.url,
          redirectUrl: msg?.redirectUrl,
        });
        return;
      }

      // redirect exactly once
      redirectedRef.current = true;

      toastAlert({
        title: 'Opening link…',
        description: (
          <span>
            If it doesn&apos;t open, please allow pop-ups for this site. Alternatively, you can{' '}
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'underline', color: 'inherit' }}
            >
              click here
            </a>{' '}
            to open it manually.
          </span>
        ),
      });

      setTimeout(() => {
        universalRedirect(url);
      }, 800);

      setTimeout(() => {
        handleEndCall();
      }, 8000);
    }

    room.on(RoomEvent.DataReceived, onDataReceived);
    return () => {
      room.off(RoomEvent.DataReceived, onDataReceived);
    };
  }, [room]);

  // Closure detection
  useEffect(() => {
    if (!isSessionActive || messages.length === 0) return;

    const last = messages[messages.length - 1];
    const lastText = (last?.message ?? '').toString();
    const signature = `${String(last?.id ?? '')}|${lastText}`;
    if (!last || signature === lastMessageSignatureRef.current) return;
    lastMessageSignatureRef.current = signature;

    const isRemote = !(last.from as any)?.isLocal;
    const text = lastText.toLowerCase();

    const closureRegex =
      /(goodbye|bye\b|take care|see you|see ya|talk to you later|until next time|have a (great|good) (day|one)|thanks for chatting|it was nice talking|thanks for stopping by)/i;

    if (closureRegex.test(text)) {

      if (!isRemote && !closureInstructionSentRef.current) {
        try {
          const lp: any = room?.localParticipant;
          if (lp && typeof lp.sendText === 'function') {
            lp.sendText('Please say exactly: "Thanks for stopping by! Have a great day"', {
              topic: 'lk.chat',
            });
            closureInstructionSentRef.current = true;
          }
        } catch (err) {
          console.warn('Failed to send closure instruction:', err);
        }
      }

      if (closureTimerRef.current) {
        clearTimeout(closureTimerRef.current);
        closureTimerRef.current = null;
      }

      if (!closureHandledRef.current) {
        closureTimerRef.current = setTimeout(() => {
          if (!closureHandledRef.current) {
            closureHandledRef.current = true;
            setTimeout(() => handleEndCall(), 0);
          }
        }, isRemote ? 3000 : 3500);
      }
    } else {
      if (isRemote && closureTimerRef.current) {
        clearTimeout(closureTimerRef.current);
        closureTimerRef.current = null;
      }
    }
  }, [messages, isSessionActive, room]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (closureTimerRef.current) clearTimeout(closureTimerRef.current);
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    };
  }, []);

  // Progress bar logic - only start AFTER warmup ends
  useEffect(() => {
    const shouldShowProgress = isSessionActive && warmupEnded && Boolean(agentVideoTrack);

    if (shouldShowProgress && !progressIntervalRef.current) {
      progressIntervalRef.current = setInterval(() => {
        setProgressBar((prev) => Math.min(prev + 1, AVATAR_INTERACTION_WINDOW_SECONDS));
      }, 1000);
    }
    // Cleanup interval when session/warmup stops or agent video track is lost
    if (!shouldShowProgress && progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
}
  }, [isSessionActive, warmupEnded, agentVideoTrack]);

  // Warning and timeout logic
  useEffect(() => {
    if (progressBar > SHOW_TIME_UP_WARNING_TIME_SECONDS && !warningScheduledRef.current) {
      warningScheduledRef.current = true;
      setFadeOut(false);

      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);

      warningTimeoutRef.current = setTimeout(() => {
        setFadeOut(true);
      }, TIME_UP_WARNING_DURATION_SECONDS * 1000);
    }

    if (progressBar >= AVATAR_INTERACTION_WINDOW_SECONDS) {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      setTimeout(() => handleEndCall(), 0);
    }
  }, [progressBar]);

  return (
    <EmbedWelcomeView
      startButtonText={callButtonText}
      onStartCall={handleStartCall}
      onEndCall={handleEndCall}
      isSessionActive={isSessionActive}
      agentVideoTrack={agentVideoTrack}
      isConnecting={isConnecting}
      progressBar={progressBar}
      fadeOut={fadeOut}
      warmupMode={isConnecting}
      warmupEnded={warmupEnded}
      onWarmupEnded={handleWarmupEnded}
    />
  );
}